/*
 * The phone numbers of the contact persons: a mobile number typed is spaced as
 * XXX XXX XXXX (e.g. 040 123 4567) and the number with the country code is made of it
 * (+358 40 123 4567), unless the user has typed that one.
 */

// The mobile numbers in Finland (04x, 050) and in Sweden (07x)
const isMobile = (digits: string) => /^(04|050|07)/.test(digits);

// Groups of three, three and the rest, e.g. 0401234567 -> 040 123 4567
const groupMobile = (digits: string) =>
  [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6)]
    .filter((part) => part !== '')
    .join(' ');

/** The phone number as typed, a mobile number spaced as XXX XXX XXXX. */
export const formatPhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (phone.trim().startsWith('+') || !isMobile(digits)) {
    return phone;
  }

  return groupMobile(digits);
};

// E.g. Suomi -> 358, Ruotsi -> 46; the country of the department when the company has none
export const getCountryCode = (
  companyCountry: null | string | undefined,
  departmentCountry: null | string | undefined
): string => {
  const country = (companyCountry ?? '').trim().toLowerCase();
  if (['suomi', 'finland', 'fi'].includes(country)) {
    return '358';
  }
  if (['ruotsi', 'sverige', 'sweden', 'se'].includes(country)) {
    return '46';
  }

  return departmentCountry === 'SE' ? '46' : '358';
};

/**
 * The phone number with the country code, e.g. 040 123 4567 -> +358 40 123 4567; a number
 * typed with its country code as it is, empty without a number.
 */
export const withCountryCode = (phone: string, countryCode: string): string => {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (digits === '') {
    return '';
  }
  if (trimmed.startsWith('+')) {
    return trimmed;
  }

  const national = digits.replace(/^0/, '');
  const spaced = isMobile(digits)
    ? [national.slice(0, 2), national.slice(2, 5), national.slice(5)]
        .filter((part) => part !== '')
        .join(' ')
    : national;

  return `+${countryCode} ${spaced}`;
};
